import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, AlertTriangle, Loader2, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { safeFormatDate, safeDistanceToNow, toDate } from "@/lib/dates";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
// pdfReport is dynamically imported at call time to keep jsPDF off the initial bundle.

export default function FeedInventoryPage() {
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [form, setForm] = useState({ item: "", quantity: "", unit: "bags", action: "remaining", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, l] = await Promise.all([api.get("/inventory/feed/items"), api.get("/inventory/feed/logs?limit=50")]);
      setItems(i.data); setLogs(l.data);
      // Fetch series for each item
      const seriesMap = {};
      for (const it of i.data) {
        try {
          const s = await api.get(`/inventory/feed/series/${it.name}?days=30`);
          seriesMap[it.name] = s.data;
        } catch {}
      }
      setSeries(seriesMap);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Reload when the global Quick Edit FAB applies changes
  useQuickEditRefresh(load);

  const submitLog = async () => {
    if (!form.item || !form.quantity) { toast.error("Item and quantity required"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/feed/logs", { ...form, quantity: parseFloat(form.quantity) });
      toast.success("Logged");
      setLogOpen(false); setForm({ item: "", quantity: "", unit: "bags", action: "remaining", notes: "" });
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const exportPdf = async () => {
    try {
      // Build per-item rows from the inventory list (current counts + projection)
      const itemRows = items.map((it) => ({
        item: (it.name || "").charAt(0).toUpperCase() + (it.name || "").slice(1),
        qty: it.current_qty != null ? `${it.current_qty} ${it.unit || "bags"}` : "",
        unit: it.unit || "bags",
        used_today: it.used_today ?? "",
        avg_daily: it.avg_daily_consumption_7d ?? it.avg_daily_consumption_14d ?? "",
        days_left: it.days_until_empty != null ? `${it.days_until_empty}` : "",
      }));
      const { downloadReport } = await import("@/lib/pdfReport");
      await downloadReport({
        eyebrow: "INVENTORY",
        title: "Feed Inventory",
        period: "Live counts and run-out projection across all feed items",
        filename: "feed-inventory",
        summary: [
          { label: "Items tracked", value: items.length },
          { label: "Alerts", value: items.filter((i) => i.alert).length },
        ],
        columns: [
          { header: "Item", dataKey: "item" },
          { header: "On hand", dataKey: "qty", mono: true, halign: "right" },
          { header: "Used today", dataKey: "used_today", mono: true, halign: "right" },
          { header: "Avg daily use", dataKey: "avg_daily", mono: true, halign: "right" },
          { header: "Days until empty", dataKey: "days_left", mono: true, halign: "right" },
        ],
        rows: itemRows,
      });
    } catch (e) {
      toast.error("Could not generate PDF");
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feed inventory"
        subtitle="Live counts from WhatsApp quick-edit + bot ingestion. with run-out projection"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportPdf}
              disabled={loading || items.length === 0}
              data-testid="feed-download-pdf-button"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          <Dialog open={logOpen} onOpenChange={setLogOpen}>
            <DialogTrigger asChild><Button data-testid="feed-log-button"><Plus className="h-4 w-4 mr-2" /> Log count</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log feed count</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Item</Label><Input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value.toLowerCase() })} placeholder="feed, lucerne, teff, hay" data-testid="feed-log-item" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Quantity</Label><Input type="number" step="0.5" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="feed-log-quantity" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Action</Label><select className="w-full h-10 rounded-md border bg-background px-3 text-sm" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} data-testid="feed-log-action">{["remaining","delivered","used"].map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
              </div>
              <DialogFooter><Button onClick={submitLog} disabled={saving} data-testid="feed-log-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {/* Items KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feed-items-grid">
        {loading ? (
          <Card className="rounded-xl p-6 text-sm text-muted-foreground">Loading…</Card>
        ) : items.length === 0 ? (
          <Card className="rounded-xl p-6 text-sm text-muted-foreground col-span-3 text-center">
            No feed items yet. Mosa can log via WhatsApp: <span className="font-mono">"8 bags feed left, 79 Lucerne"</span>
          </Card>
        ) : items.map((it) => (
          <Card key={it.id || it.name} className={`rounded-xl ${it.alert ? "border-[hsl(var(--ap-warning)/0.5)] bg-[hsl(var(--ap-warning)/0.05)]" : ""}`} data-testid={`feed-item-card-${it.name}`}>
            <CardHeader className="pb-2">
              <div className="flex items-baseline justify-between">
                <CardTitle className="text-base font-medium capitalize">{it.name}</CardTitle>
                {it.alert && <AlertTriangle className="h-4 w-4 text-[hsl(var(--ap-warning))]" />}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline justify-between">
                <span className="font-serif text-4xl tabular-nums" data-testid={`feed-item-qty-${it.name}`}>
                  {it.has_remaining_log ? it.current_qty : ""}
                </span>
                <span className="text-xs text-muted-foreground">
                  {it.has_remaining_log ? `${it.unit} remaining` : `no count logged yet`}
                </span>
              </div>

              {/* Last updated + used today + days-until-empty */}
              <div className="mt-4 pt-3 border-t space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Last updated
                  </span>
                  <span className="text-xs text-foreground" data-testid={`feed-item-last-updated-${it.name}`}>
                    {it.last_updated_at
                      ? `${safeDistanceToNow(it.last_updated_at, {}, "")} ago`
                      : "never"}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">Used today</span>
                  <span className="text-xs font-mono tabular-nums" data-testid={`feed-item-used-today-${it.name}`}>
                    {it.used_today != null ? `${it.used_today} ${it.unit}` : ""}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">Days until empty</span>
                  <span className={`font-mono tabular-nums text-sm ${it.alert ? "text-[hsl(var(--ap-warning))]" : ""}`} data-testid={`feed-item-days-${it.name}`}>
                    {it.days_until_empty ?? ""}
                  </span>
                </div>
                {it.hay_backup_available && (
                  <div className="text-[10px] text-muted-foreground italic">
                    Hay supply covered by the other hay category.
                  </div>
                )}
              </div>

              {series[it.name] && series[it.name].length > 1 && (() => {
                // Pre-compute the chart series once per render — using an
                // IIFE + slice keeps this cheap and avoids the linter's
                // "filter/map inside render" complaint while remaining
                // stable-key friendly for Recharts.
                const seriesData = series[it.name]
                  .map((d) => ({ x: toDate(d.created_at)?.getTime(), q: d.quantity }))
                  .filter((p) => p.x != null);
                return (
                <div className="h-[80px] mt-3">
                  <ResponsiveContainer>
                    <LineChart data={seriesData}>
                      <Line type="monotone" dataKey="q" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                      <XAxis
                        dataKey="x"
                        type="number"
                        domain={["dataMin", "dataMax"]}
                        scale="time"
                        hide
                      />
                      <YAxis hide domain={[0, "dataMax"]} />
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11 }}
                        labelFormatter={(l) => safeFormatDate(l, "MMM d, HH:mm")}
                        formatter={(value) => [value, it.unit]}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                );
              })()}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent logs */}
      <Card className="rounded-xl">
        <CardHeader><CardTitle className="text-base">Recent logs</CardTitle></CardHeader>
        <CardContent>
          <div className="hidden md:block overflow-auto" data-testid="feed-logs-table">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Action</th>
                  <th className="text-left px-3 py-2">By</th>
                  <th className="text-left px-3 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {logs.slice(0, 30).map((l) => (
                  <tr key={l.id} className="border-b hover:bg-accent/40">
                    <td className="px-3 py-2 text-muted-foreground">{safeFormatDate(l.created_at, "MMM d, HH:mm")}</td>
                    <td className="px-3 py-2 capitalize">{l.item}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{l.quantity} <span className="text-muted-foreground">{l.unit}</span></td>
                    <td className="px-3 py-2 text-muted-foreground">{l.action}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.logged_by}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile log cards */}
          <div className="md:hidden divide-y -mx-2" data-testid="feed-logs-cards">
            {logs.slice(0, 30).map((l) => (
              <div key={l.id} className="px-2 py-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="font-medium capitalize">{l.item}</span>{" "}
                    <span className="text-xs text-muted-foreground capitalize">· {l.action}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-mono tabular-nums text-sm">{l.quantity}</span>{" "}
                    <span className="text-xs text-muted-foreground">{l.unit}</span>
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
                  <span>{safeFormatDate(l.created_at, "MMM d, HH:mm")}</span>
                  {l.logged_by && <><span>·</span><span>{l.logged_by}</span></>}
                  {l.source && <><span>·</span><span className="uppercase tracking-wider">{l.source}</span></>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Conversational quick-edit FAB is rendered globally in AppShell. pages
          just listen on QUICK_EDIT_EVENT to refresh themselves. */}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono tabular-nums">{value ?? ""}</div>
    </div>
  );
}
